import React, { useEffect, useRef } from 'react';
import { Table as AntTable, Empty, TableProps } from 'antd';
import { useTranslation } from '@refinedev/core';
import { ColumnType } from '@/types';

interface DataTableProps<T> extends Omit<TableProps<T>, 'pagination'> {
  columns: ColumnType[];
  data?: { data: T; headers?: { 'x-total-count'?: number } };
  loading?: boolean;
  pageSize?: number;
  currentPage?: number;
  onPageChange?: (page: number) => void;
}

export const DataTable = <T extends object>({
  columns,
  data,
  loading,
  pageSize = 10,
  currentPage = 1,
  onPageChange,
  ...tableProps
}: DataTableProps<T>) => {
  const { translate } = useTranslation();
  const dataSource = Array.isArray(data?.data) ? data.data : [];
  const reportedTotal = data?.headers?.['x-total-count'];

  // The server's reported total-count can overstate how many pages actually
  // have data (its count query and its list query aren't always in sync),
  // which would otherwise leave the pager offering a page that renders
  // empty. Rather than try to predict that ahead of time — which means
  // extra speculative requests that can themselves be misread if the
  // backend is briefly unwell — react only to what we've actually loaded:
  // once a page we're really on comes back empty, remember that as the
  // boundary and step back automatically instead of stranding the user on
  // "No data found".
  const knownEmptyPageRef = useRef<number | null>(null);
  const reportedTotalRef = useRef(reportedTotal);

  // `reportedTotal` goes briefly undefined while the next page loads (data
  // is cleared in between fetches), so only treat a change between two
  // *known* totals as a new query — ignoring the transient undefined keeps
  // the remembered boundary from being wiped out on every navigation.
  if (
    reportedTotal !== undefined &&
    reportedTotalRef.current !== undefined &&
    reportedTotalRef.current !== reportedTotal
  ) {
    knownEmptyPageRef.current = null;
  }
  if (reportedTotal !== undefined) {
    reportedTotalRef.current = reportedTotal;
  }

  if (!loading && dataSource.length === 0 && currentPage > 1) {
    knownEmptyPageRef.current = Math.min(
      knownEmptyPageRef.current ?? Infinity,
      currentPage
    );
  }

  const lastKnownTotal = reportedTotal ?? reportedTotalRef.current;
  const total =
    lastKnownTotal !== undefined && knownEmptyPageRef.current !== null
      ? Math.min(lastKnownTotal, (knownEmptyPageRef.current - 1) * pageSize)
      : lastKnownTotal;

  useEffect(() => {
    if (!loading && dataSource.length === 0 && currentPage > 1) {
      onPageChange?.(currentPage - 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, dataSource.length, currentPage]);

  return (
    <div className="w-full">
      <AntTable
        {...tableProps}
        rowKey="id"
        columns={columns}
        dataSource={dataSource}
        loading={loading}
        scroll={{ x: 'max-content' }}
        pagination={{
          total,
          position: ['bottomCenter'],
          pageSize: pageSize || 10,
          current: currentPage,
          showSizeChanger: false,
          onChange: onPageChange,
        }}
        locale={{
          emptyText: <Empty description={translate('noData')} />,
        }}
      />
    </div>
  );
};
